/**
 * useContactsListStore — état de la page V2 « Contacts »
 *
 * Liste paginée côté serveur (offset/limit + total), recherche contact-only
 * débouncée, tri par colonne. La recherche annule la requête en vol
 * (AbortController) — comme l'omnisearch et la page Clients.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  listContacts,
  type ContactListItem,
  type ContactSortField,
  type SortOrder,
} from '@/api/contactsApi'

const PAGE_SIZE = 25
const DEBOUNCE_MS = 400

export const useContactsListStore = defineStore('contactsList', () => {
  const q = ref('')
  const page = ref(1) // 1-based
  const pageSize = ref(PAGE_SIZE)
  // Défaut : nom (lastname) croissant (A→Z).
  const sort = ref<ContactSortField>('lastname')
  const order = ref<SortOrder>('asc')

  const rows = ref<ContactListItem[]>([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref(false)

  let abort: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
  const from = computed(() => (total.value === 0 ? 0 : (page.value - 1) * pageSize.value + 1))
  const to = computed(() => Math.min(page.value * pageSize.value, total.value))

  async function fetch() {
    abort?.abort()
    abort = new AbortController()
    loading.value = true
    error.value = false
    try {
      const res = await listContacts({
        q: q.value.trim() || undefined,
        limit: pageSize.value,
        offset: (page.value - 1) * pageSize.value,
        sort: sort.value,
        order: order.value,
        signal: abort.signal,
      })
      rows.value = res.data
      total.value = res.total
      loading.value = false
    } catch (e: unknown) {
      // AbortError = requête annulée volontairement (nouvelle frappe) → pas une erreur
      if (e instanceof Error && e.name === 'AbortError') return
      error.value = true
      loading.value = false
    }
  }

  function reloadNow() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    void fetch()
  }

  function setQuery(value: string) {
    q.value = value
    page.value = 1
    if (debounceTimer) clearTimeout(debounceTimer)
    loading.value = true
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void fetch()
    }, DEBOUNCE_MS)
  }

  function setPage(p: number) {
    const next = Math.min(Math.max(1, p), pageCount.value)
    if (next === page.value) return
    page.value = next
    reloadNow()
  }

  /** Clic en-tête : même colonne → inverse le sens ; nouvelle colonne → asc. */
  function setSort(field: ContactSortField) {
    if (sort.value === field) {
      order.value = order.value === 'asc' ? 'desc' : 'asc'
    } else {
      sort.value = field
      order.value = 'asc'
    }
    page.value = 1
    reloadNow()
  }

  /** Entrée sur la page : reset + 1er chargement. `initialQuery` vient de /contacts?q=… */
  function init(initialQuery?: string) {
    q.value = initialQuery?.trim() ?? ''
    page.value = 1
    sort.value = 'lastname'
    order.value = 'asc'
    void fetch()
  }

  return {
    q,
    page,
    pageSize,
    sort,
    order,
    rows,
    total,
    loading,
    error,
    pageCount,
    from,
    to,
    setQuery,
    setPage,
    setSort,
    init,
    reload: reloadNow,
  }
})
